import { useState, useEffect } from "react";
import api from "../../api";

/**
 * The group → week / subject → topic → subtopic cascade, for one selected group.
 *
 * Both the upload dialog and the content filter bar need the same four lists, keyed on a different
 * group each. Keeping the fetches here means the two cannot drift apart, and it enforces the rule
 * the server also enforces: weeks and subjects belong to the plan, and a topic belongs to a
 * subject — so anything downstream of a changed parent is dropped rather than left stale.
 *
 * Subtopics arrive nested inside their topic, so selecting a topic costs no extra request.
 */
export default function useGroupTaxonomy(planId, subjectId, topicId) {
  const [weeks, setWeeks] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    if (!planId) {
      setWeeks([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`tutor/groups/${planId}/weeks`)
      .then((res) => {
        if (!cancelled) setWeeks(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setWeeks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [planId]);

  // Subjects are those with course content for the group's school year, so the list is
  // group-specific and must reload with the group.
  useEffect(() => {
    if (!planId) {
      setSubjects([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`tutor/groups/${planId}/subjects`)
      .then((res) => {
        if (!cancelled) setSubjects(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setSubjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [planId]);

  useEffect(() => {
    if (!planId || !subjectId) {
      setTopics([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`tutor/groups/${planId}/topics`, { params: { subject_id: subjectId } })
      .then((res) => {
        if (!cancelled) setTopics(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setTopics([]);
      });
    return () => {
      cancelled = true;
    };
  }, [planId, subjectId]);

  const subtopics = topics.find((t) => t.id === topicId)?.subtopics || [];

  return { weeks, subjects, topics, subtopics };
}
